import { Suspense, lazy, useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useAiChatPanel } from '@/contexts/AiChatPanelContext'
import { useAuthStore } from '@/app/store/auth'

const ChatApp = lazy(
    () => import('@/features/ai-chat/core/components/ChatApp')
)

/**
 * What closing the panel would destroy, as reported by ChatApp, and the warning
 * that names it. Closing unmounts the chat, so none of it can be recovered.
 * Kept in step with CLOSE_CONFIRM_KEYS in ChatApp.jsx (which guards its own
 * X / open-in-new-tab buttons); duplicated rather than imported so this file
 * does not pull the lazy ChatApp chunk into the shell.
 */
type CloseRisk = 'clara' | 'answer' | 'both'

const CLOSE_CONFIRM_KEYS: Record<CloseRisk, string> = {
    clara: 'aiChat.modals.confirmCloseClara',
    answer: 'aiChat.modals.confirmCloseAnswer',
    both: 'aiChat.modals.confirmCloseClaraAndAnswer',
}

function PanelLoadingFallback() {
    const { t } = useTranslation()
    return (
        <div className="flex h-full items-center justify-center">
            <div className="text-center">
                <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                <p className="text-muted-foreground">
                    {t('aiChat.connecting')}
                </p>
            </div>
        </div>
    )
}

/**
 * AiChatPanel — slides-in the standalone AI Chat as a fixed overlay.
 *
 * Uses createPortal to render outside AppShell's DOM tree.
 * Two-phase mount/unmount:
 *   1. `isOpen` goes true  → mount the portal, then on next frame set `visible` to trigger slide-in
 *   2. `isOpen` goes false → set `visible` false to trigger slide-out, unmount after transition ends
 */
export function AiChatPanel() {
    const { t } = useTranslation()
    const { isOpen, close, targetSessionId, pendingMessage, targetTab, injectedContext, clearTargetSession } = useAiChatPanel()
    const user = useAuthStore((state) => state.user)
    const socketUrl = import.meta.env.VITE_AI_SOCKET_URL || ''

    const [panelWidth, setPanelWidth] = useState(780)

    const handlePanelWidthChange = useCallback((newWidth: number) => {
        setPanelWidth(newWidth)
    }, [])

    // --- Slide animation state ---
    const [mounted, setMounted] = useState(false)  // controls DOM presence
    const [visible, setVisible] = useState(false)   // controls CSS transform
    const panelRef = useRef<HTMLDivElement>(null)

    // Phase 1: isOpen → mount, then trigger slide-in on next frame
    useEffect(() => {
        if (isOpen) {
            setMounted(true)
            // Wait one frame so the off-screen position renders first, then animate in
            requestAnimationFrame(() => {
                requestAnimationFrame(() => setVisible(true))
            })
        } else {
            // Trigger slide-out
            setVisible(false)
        }
    }, [isOpen])

    // Phase 2: after slide-out transition ends, unmount
    const handleTransitionEnd = useCallback(() => {
        if (!visible && !isOpen) {
            setMounted(false)
        }
    }, [visible, isOpen])

    // Lock body scroll when panel is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden'
            return () => { document.body.style.overflow = '' }
        }
    }, [isOpen])

    // Whatever ChatApp says would be lost if it were unmounted right now.
    const [closeRisk, setCloseRisk] = useState<CloseRisk | null>(null)
    const handleCloseRiskChange = useCallback((risk: CloseRisk | null) => {
        setCloseRisk(risk)
    }, [])

    // Click-outside close. It has to ask the same question ChatApp's own X asks:
    // an accidental click on the app behind the panel must not silently end a live
    // Clara session (screen share + microphone) or bin an answer still streaming.
    const requestClose = useCallback(() => {
        if (closeRisk && !window.confirm(t(CLOSE_CONFIRM_KEYS[closeRisk]))) return
        close()
    }, [closeRisk, close, t])

    const handleOpenInNewTab = () => {
        window.open('/ai-chat', '_blank')
        close()
    }

    if (!mounted) return null

    return createPortal(
        <>
            {/* Backdrop — fades in/out, and closes on click like the dimmed veil
                says it will. Closing unmounts ChatApp, which destroys any answer
                still streaming and a live Clara session (screen share and
                microphone included), so the click goes through requestClose:
                it confirms when there is something to lose and closes straight
                away when there is not. */}
            <div
                aria-hidden
                className="fixed inset-0 z-[200] bg-black/30"
                style={{
                    top: 'var(--topbar-height, 3.5rem)',
                    opacity: visible ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                    // Don't let the invisible, fading-out backdrop swallow a click
                    // aimed at the app during the 0.3s slide-out.
                    pointerEvents: visible ? 'auto' : 'none',
                }}
                onClick={requestClose}
            />

            {/* Fixed-position shell — slides in from the right */}
            <div
                ref={panelRef}
                onTransitionEnd={handleTransitionEnd}
                style={{
                    position: 'fixed',
                    top: 'var(--topbar-height, 3.5rem)',
                    right: 0,
                    zIndex: 210,
                    width: `${panelWidth}px`,
                    maxWidth: '92vw',
                    height: 'calc(100vh - var(--topbar-height, 3.5rem))',
                    overflow: 'hidden',
                    transform: visible ? 'translateX(0)' : 'translateX(100%)',
                    transition: 'transform 0.3s ease',
                }}
            >
                {/* ChatApp — panel controls are rendered inside ChatApp when panelMode is true */}
                <div className="h-full w-full ai-panel-mode">
                    <Suspense fallback={<PanelLoadingFallback />}>
                        <ChatApp
                            userId={user?.id}
                            apiBaseUrl=""
                            socketUrl={socketUrl}
                            panelMode
                            onPanelClose={close}
                            onOpenInNewTab={handleOpenInNewTab}
                            onPanelWidthChange={handlePanelWidthChange}
                            targetSessionId={targetSessionId}
                            pendingMessage={pendingMessage}
                            targetTab={targetTab}
                            injectedContext={injectedContext}
                            onTargetSessionConsumed={clearTargetSession}
                            onCloseRiskChange={handleCloseRiskChange}
                        />
                    </Suspense>
                </div>
            </div>
        </>,
        document.body
    )
}
